import React, { useRef, useState } from 'react';
import { Upload, X, FileText, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

/**
 * Generic file upload field with client-side validation UI.
 * Does NOT perform the upload itself — calls onChange with validated files.
 * Actual upload must traverse geo-api in the parent component.
 */

export interface UploadFieldProps {
  id: string;
  label: string;
  description?: string;
  accept?: string;            // mime/extensions, e.g. 'application/pdf,.pdf'
  maxSizeMb?: number;         // per-file limit
  multiple?: boolean;
  required?: boolean;
  value?: File[];
  onChange: (files: File[]) => void;
}

const DEFAULT_MAX_MB = 10;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function matchesAccept(file: File, accept?: string): boolean {
  if (!accept) return true;
  const tokens = accept.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return tokens.some((t) => {
    if (t.startsWith('.')) return name.endsWith(t);
    if (t.endsWith('/*')) return type.startsWith(t.slice(0, -1));
    return type === t;
  });
}

export const UploadField: React.FC<UploadFieldProps> = ({
  id,
  label,
  description,
  accept,
  maxSizeMb = DEFAULT_MAX_MB,
  multiple = false,
  required,
  value = [],
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const validate = (files: File[]): { ok: File[]; error: string | null } => {
    const ok: File[] = [];
    for (const f of files) {
      if (!matchesAccept(f, accept)) {
        return { ok: [], error: `"${f.name}" is not an accepted file type.` };
      }
      if (f.size > maxSizeMb * 1024 * 1024) {
        return {
          ok: [],
          error: `"${f.name}" exceeds the ${maxSizeMb} MB limit.`,
        };
      }
      ok.push(f);
    }
    return { ok, error: null };
  };

  const handleFiles = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;
    const list = Array.from(incoming);
    const { ok, error: err } = validate(list);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    onChange(multiple ? [...value, ...ok] : ok.slice(0, 1));
  };

  const remove = (idx: number) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
    setError(null);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium text-eds-fg">
        {label}
        {required ? <span className="text-eds-danger ml-0.5" aria-hidden="true">*</span> : null}
      </Label>
      {description ? (
        <p className="text-sm font-medium text-eds-muted-fg">{description}</p>
      ) : null}

      <div
        className="border border-dashed border-eds-border rounded-eds-md p-4 bg-eds-surface-2 hover:bg-eds-surface-3 transition-colors focus-within:ring-2 focus-within:ring-eds-ring"
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          handleFiles(e.dataTransfer.files);
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-eds-muted-fg">
            <Upload className="h-4 w-4 text-eds-muted-fg" aria-hidden="true" />
            <span>
              Drag &amp; drop or
              <button
                type="button"
                className="ml-1 text-eds-success underline underline-offset-2 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring rounded-eds-md"
                onClick={() => inputRef.current?.click()}
              >
                browse
              </button>
            </span>
          </div>
          <span className="text-xs text-eds-muted-fg">
            {accept ? `${accept} · ` : ''}max {maxSizeMb} MB{multiple ? ' each' : ''}
          </span>
        </div>
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          multiple={multiple}
          required={required && value.length === 0}
          className="sr-only"
          onChange={(e) => handleFiles(e.target.files)}
          aria-describedby={error ? `${id}-error` : undefined}
          aria-invalid={!!error}
        />
      </div>

      {error ? (
        <p
          id={`${id}-error`}
          role="alert"
          className="flex items-center gap-1.5 text-xs text-eds-danger"
        >
          <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
          {error}
        </p>
      ) : null}

      {value.length > 0 ? (
        <ul className="space-y-1.5" aria-label="Selected files">
          {value.map((f, idx) => (
            <li
              key={`${f.name}-${idx}`}
              className="flex items-center justify-between gap-2 bg-eds-surface border border-eds-border rounded-eds-sm px-2.5 py-1.5"
            >
              <span className="flex items-center gap-2 text-xs text-eds-fg min-w-0">
                <FileText className="h-3.5 w-3.5 text-eds-muted-fg shrink-0" aria-hidden="true" />
                <span className="truncate">{f.name}</span>
                <span className="text-eds-muted-fg shrink-0">{formatSize(f.size)}</span>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => remove(idx)}
                aria-label={`Remove ${f.name}`}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default UploadField;
